import { createWithEqualityFn as create } from 'zustand/traditional'
import { shallow } from 'zustand/vanilla/shallow'

import {
	FlagSearchDto,
	GeolocationDto,
	GuestQuantityDto,
	IntegrationsSearchDto,
	PaginationDto,
	StayPeriodDate
} from '@wwt/entities/api/search-response'

import {
	SearchRequest,
	SearchRequestErrorElement,
	SearchRequestErrorType,
	SearchRequestStayMatrixElement
} from '@/entities/search'
import { createSelectors } from '@/shared/lib/createSelectors.ts'

import { useFiltersStore } from '../filters'
import { defaultErrors } from './searchRequestDefaultState.ts'

type State = {
	/**
	 * Before using this value - it must be initialized via `setNewState`
	 *
	 * Initialization by creating a page when parsing data from **URLSearchParams**
	 *
	 * Also, when parsing URLSearchParams, **default** data is automatically set
	 *
	 * @author Ivan Shchedrovskyi
	 */
	mainRequestState: SearchRequest | null

	/**
	 * Errors in search request
	 *
	 * @author Ivan Shchedrovskyi
	 */
	errors: Record<SearchRequestErrorType, SearchRequestErrorElement | null>
}

interface Actions {
	setNewState: (newState: SearchRequest) => void
	setPartialState: (partialState: Partial<SearchRequest>) => void
	setPagination: (newPagination: PaginationDto) => void
	setStayDefaultPeriod: (data: StayPeriodDate) => void
	setStayDateMatrix: (data: SearchRequestStayMatrixElement) => void
	setGeolocation: (newGeolocation: GeolocationDto) => void
	setGuestsQuantity: (newGuestsQuantity: GuestQuantityDto) => void
	setSort: (newSort: string) => void
	setFlags: (newFlags: FlagSearchDto) => void
	setIntegrationSearch: (integrationSearch: IntegrationsSearchDto) => void
	get: () => SearchRequest | null

	/**
	 * Set error in search request
	 *
	 * @param type - Type of error, like destination or calendar
	 * @param error - Error title and message
	 */
	setError: (
		type: SearchRequestErrorType,
		error: SearchRequestErrorElement | null
	) => void

	/**
	 * Set array of errors in search requst
	 * @param errors
	 */
	setErrors: (
		errors: {
			type: SearchRequestErrorType
			error: SearchRequestErrorElement | null
		}[]
	) => void

	/**
	 * Get all errors
	 */
	getErrors: () => SearchRequestErrorElement[]

	/**
	 * Check if there are any errors
	 *
	 * Used for prevent requests to server, etc
	 */
	isError: (type?: SearchRequestErrorType) => boolean

	/**
	 * Clean all errors
	 */
	cleanErrors: () => void

	/**
	 * Get first error
	 *
	 * Temp solution for show only one error
	 * In future we need to show all errors
	 *
	 * @deprecated
	 */
	getError: () => SearchRequestErrorElement | null
}

export type SearchRequestStore = State & Actions

export const useSearchRequestBase = create<State & Actions>(
	(set, get) => ({
		mainRequestState: null,
		// TODO: Remove structuredClone
		// eslint-disable-next-line
		errors: structuredClone(defaultErrors),

		get: () => {
			const state = get().mainRequestState

			if (state === null) {
				return null
			}

			return {
				...state,
				/**
				 * Filters are stored separately
				 *
				 * But we need filters get filters value for send to API
				 */
				filters: useFiltersStore.getState().getResultValue()
			}
		},

		setNewState: newState => {
			set(() => {
				const { filters, ...otherNewState } = newState

				/**
				 * Filters are stored separately
				 *
				 * On store value init we need to initialize filters store too
				 */
				if (filters) {
					useFiltersStore.getState().setFromActiveFilters(filters)
				}

				return {
					mainRequestState: otherNewState,
					errors: defaultErrors
				}
			})
		},

		setPartialState: partialState => {
			set(state => {
				if (state.mainRequestState === null) {
					throw new Error(
						'Not allowed to set value in search request store before initialization'
					)
				}

				const updatedState = {
					...state.mainRequestState,
					...partialState
				}

				/**
				 * Filters are stored separately
				 *
				 * On store value init we need to initialize filters store too
				 */
				if (partialState.filters) {
					useFiltersStore.getState().setFromActiveFilters(partialState.filters)
				}

				return {
					mainRequestState: updatedState,
					errors: defaultErrors
				}
			})
		},

		setPagination: newPagination => {
			set(state => {
				if (state.mainRequestState === null) {
					throw new Error(
						'Not allowed to set value in search request store before initialization'
					)
				}

				return {
					mainRequestState: {
						...state.mainRequestState,
						pagination: newPagination
					}
				}
			})
		},

		setStayDefaultPeriod: (data: StayPeriodDate) => {
			set(state => {
				if (state.mainRequestState === null) {
					throw new Error(
						'Not allowed to set value in search request store before initialization'
					)
				}

				return {
					mainRequestState: {
						...state.mainRequestState,
						stay: {
							...state.mainRequestState.stay,
							defaultStayPeriod: data
						}
					},
					errors: {
						...state.errors,
						[SearchRequestErrorType.CALENDAR]: null
					}
				}
			})
		},

		setStayDateMatrix: (data: SearchRequestStayMatrixElement) => {
			set(state => {
				if (state.mainRequestState === null) {
					throw new Error(
						'Not allowed to set value in search request store before initialization'
					)
				}

				return {
					mainRequestState: {
						...state.mainRequestState,
						stay: {
							...state.mainRequestState.stay,
							dateMatrix: data
						}
					},
					errors: {
						...state.errors,
						[SearchRequestErrorType.CALENDAR]: null
					}
				}
			})
		},

		setGeolocation: (newGeolocation: GeolocationDto) => {
			set(state => {
				if (state.mainRequestState === null) {
					throw new Error(
						'Not allowed to set value in search request store before initialization'
					)
				}

				return {
					mainRequestState: {
						...state.mainRequestState,
						geolocation: newGeolocation
					}
				}
			})
		},

		setGuestsQuantity: newGuestsQuantity => {
			set(state => {
				if (state.mainRequestState === null) {
					throw new Error(
						'Not allowed to set value in search request store before initialization'
					)
				}

				return {
					mainRequestState: {
						...state.mainRequestState,
						guestQuantity: newGuestsQuantity
					}
				}
			})
		},

		setSort: (newSort: string) => {
			set(state => {
				if (state.mainRequestState === null) {
					throw new Error(
						'Not allowed to set value in search request store before initialization'
					)
				}

				return {
					mainRequestState: { ...state.mainRequestState, sortBy: newSort }
				}
			})
		},

		setFlags: (newFlags: FlagSearchDto) => {
			set(state => {
				if (state.mainRequestState === null) {
					throw new Error(
						'Not allowed to set value in search request store before initialization'
					)
				}

				return {
					mainRequestState: { ...state.mainRequestState, flagSearch: newFlags }
				}
			})
		},

		setIntegrationSearch: (newIntegrationSearch: IntegrationsSearchDto) => {
			set(state => {
				if (state.mainRequestState === null) {
					throw new Error(
						'Not allowed to set value in search request store before initialization'
					)
				}

				return {
					mainRequestState: {
						...state.mainRequestState,
						integrationSearch: newIntegrationSearch
					}
				}
			})
		},

		setError: (type, error) =>
			set(state => ({
				errors: { ...state.errors, [type]: error }
			})),

		setErrors: errors =>
			set(state => {
				const errorsForState = errors.reduce((acc, { type, error }) => {
					return {
						...acc,
						[type]: error
					}
				}, {})

				return {
					errors: {
						...state.errors,
						...errorsForState
					}
				}
			}),

		getErrors: () => {
			return Object.values(get().errors).filter(error => error !== null)
		},

		getError: () => {
			const validErrors = Object.values(get().errors).filter(
				error => error !== null
			)

			return validErrors[0]
		},

		isError: type => {
			if (type) {
				return get().errors[type] !== null
			}

			return Object.values(get().errors).some(error => error !== null)
		},

		cleanErrors: () =>
			set(() => ({
				errors: defaultErrors
			}))
	}),
	shallow
)

export const useSearchRequest = createSelectors(useSearchRequestBase)
